import type { BackgroundMode, BeadColor, Difficulty, EditorTool, PatternMode, TemplatePattern } from './types';

export type Locale = 'en' | 'zh-CN';

type MessageValue = string | number;

const en = {
  documentTitle: 'Bead Studio AI',
  documentDescription: 'AI-assisted fuse bead pattern maker for editable, printable bead designs.',
  language: 'Language',
  patternDesigner: 'Pattern Designer',
  primaryActions: 'Primary actions',
  uploadImage: 'Upload Image',
  browseTemplates: 'Browse Templates',
  tryQuickMode: 'Try Quick Mode',
  save: 'Save',
  exportPng: 'Export PNG',
  chooseImage: 'Choose image',
  uploadFallback: 'PNG, JPEG, or WebP',
  uploadedSourcePreview: 'Uploaded source preview',
  dropImage: 'Drop image or choose file',
  setup: 'Setup',
  newBlankGrid: 'New blank grid',
  gridSize: 'Grid size',
  maximumColors: 'Maximum colors',
  generationMode: 'Generation mode',
  background: 'Background',
  rotate: 'Rotate',
  rotateLeft: 'Rotate left',
  rotateRight: 'Rotate right',
  cropZoom: 'Crop zoom',
  cleanupIsolatedCells: 'Cleanup isolated cells',
  aiAssist: 'AI Assist',
  structuredRecommendation: 'Structured recommendation',
  runLocalAiAssist: 'Run local AI assist',
  recommendationMeta: '{width}x{height} · {colors} colors',
  useTheseSettings: 'Use these settings',
  noRecommendationYet: 'No recommendation yet',
  starterTemplates: 'Starter templates',
  templateCategories: 'Template categories',
  templatePreview: '{title} preview',
  starterPatternCount: '{count} starter patterns',
  patternEditor: 'Pattern editor',
  patternMeta: '{width} x {height} · {colors} colors · {beads} beads',
  undo: 'Undo',
  redo: 'Redo',
  zoom: 'Zoom',
  toggleGrid: 'Toggle grid',
  toggleSymbols: 'Toggle symbols',
  beadGridEditor: '{title} bead grid editor',
  canvasCellSize: '{size}px cells',
  tools: 'Tools',
  palette: 'Palette',
  colorCount: '{count} colors',
  selectedColorTitle: '{name} ({id})',
  replaceColor: 'Replace Color',
  globalSwap: 'Global swap',
  replace: 'Replace',
  beadCounts: 'Bead Counts',
  totalBeads: '{count} total',
  noBeadsYet: 'No beads yet',
  export: 'Export',
  exportTypes: 'PNG · JSON · CSV · PDF',
  gridLines: 'Grid lines',
  symbols: 'Symbols',
  myDesigns: 'My Designs',
  savedLocallyCount: '{count} saved locally',
  saveCurrentDesign: 'Save current design',
  savedDesignMeta: '{width}x{height} · {beads} beads',
  duplicateDesign: 'Duplicate {title}',
  deleteDesign: 'Delete {title}',
  noSavedDesigns: 'No saved designs',
  ready: 'Ready',
  localDraftPending: 'Local draft pending',
  unableReadLocalDesigns: 'Unable to read local designs',
  recoveredLocalDraft: 'Recovered local draft',
  draftStorageUnavailable: 'Draft storage unavailable',
  savedLocallyAt: 'Saved locally {time}',
  localAutosaveFailed: 'Local autosave failed',
  unsupportedFileType: 'Unsupported file type',
  fileTooLarge: 'File is larger than the 8 MB prototype limit',
  imageLoaded: 'Image loaded',
  imageDecodeFailed: 'Image could not be decoded',
  chooseImageFirst: 'Choose an image first',
  uploadedPattern: 'Uploaded pattern',
  patternGenerated: 'Pattern generated',
  generationFailed: 'Generation failed',
  templateLoaded: 'Template loaded',
  recommendationApplied: 'Recommendation applied',
  colorReplaced: 'Color replaced',
  designSaved: 'Design saved',
  designLoaded: 'Design loaded',
  designDuplicated: 'Design duplicated',
  designDeleted: 'Design deleted',
  blankGridCreated: 'Blank grid created',
  copyTitle: '{title} copy',
  untitledBeadPattern: 'Untitled bead pattern',
  fillRegion: 'fill region',
  eraseCells: 'erase cells',
  paintCells: 'paint cells',
  pdfMeta: '{width} x {height} beads · {total} total beads',
  pdfBeadCounts: 'Bead counts',
  pdfFooter: 'Preview and counts generated locally by Bead Studio AI.',
  csvColorId: 'color_id',
  csvColorName: 'color_name',
  csvBrand: 'brand',
  csvHex: 'hex',
  csvSymbol: 'symbol',
  csvQuantity: 'quantity',
  aiSubjectUpload: 'uploaded craft image',
  aiSubjectTemplate: 'selected template',
  aiReasonLarge: 'The source appears detailed, so a larger grid will preserve recognizable edges.',
  aiReasonBalanced: 'This size balances detail with a beginner-friendly bead count.',
  aiColorReasonMore: 'More colors should reduce muddy areas before manual cleanup.',
  aiColorReasonCurrent: 'The current color limit is suitable for a clean craft pattern.',
  aiFeatureSilhouette: 'main silhouette',
  aiFeatureEdges: 'high-contrast edges',
  aiFeatureDetails: 'large facial or logo details',
  aiStrategyCrop: 'crop tightly around the subject',
  aiStrategyMerge: 'merge small noisy regions',
  aiStrategyPalette: 'keep final colors bead-palette safe',
  aiWarningLarge: 'Detailed photos may need manual cleanup after Quick Mode generation.'
};

type MessageKey = keyof typeof en;

const zh: Record<MessageKey, string> = {
  documentTitle: 'Bead Studio AI',
  documentDescription: 'AI 辅助的拼豆图案制作工具，可编辑、可打印。',
  language: '语言',
  patternDesigner: '图案设计器',
  primaryActions: '主要操作',
  uploadImage: '上传图片',
  browseTemplates: '浏览模板',
  tryQuickMode: '试用快速模式',
  save: '保存',
  exportPng: '导出 PNG',
  chooseImage: '选择图片',
  uploadFallback: 'PNG、JPEG 或 WebP',
  uploadedSourcePreview: '上传源图预览',
  dropImage: '拖放图片或选择文件',
  setup: '设置',
  newBlankGrid: '新建空白网格',
  gridSize: '网格尺寸',
  maximumColors: '最多颜色',
  generationMode: '生成模式',
  background: '背景',
  rotate: '旋转',
  rotateLeft: '向左旋转',
  rotateRight: '向右旋转',
  cropZoom: '裁剪缩放',
  cleanupIsolatedCells: '清理孤立格',
  aiAssist: 'AI 辅助',
  structuredRecommendation: '结构化建议',
  runLocalAiAssist: '运行本地 AI 辅助',
  recommendationMeta: '{width}x{height} · {colors} 种颜色',
  useTheseSettings: '使用这些设置',
  noRecommendationYet: '暂无建议',
  starterTemplates: '入门模板',
  templateCategories: '模板分类',
  templatePreview: '{title} 预览',
  starterPatternCount: '{count} 个入门图案',
  patternEditor: '图案编辑器',
  patternMeta: '{width} x {height} · {colors} 种颜色 · {beads} 颗珠子',
  undo: '撤销',
  redo: '重做',
  zoom: '缩放',
  toggleGrid: '切换网格',
  toggleSymbols: '切换符号',
  beadGridEditor: '{title} 拼豆网格编辑器',
  canvasCellSize: '{size}px 单元格',
  tools: '工具',
  palette: '色板',
  colorCount: '{count} 种颜色',
  selectedColorTitle: '{name} ({id})',
  replaceColor: '替换颜色',
  globalSwap: '全局替换',
  replace: '替换',
  beadCounts: '珠子统计',
  totalBeads: '共 {count} 颗',
  noBeadsYet: '还没有珠子',
  export: '导出',
  exportTypes: 'PNG · JSON · CSV · PDF',
  gridLines: '网格线',
  symbols: '符号',
  myDesigns: '我的设计',
  savedLocallyCount: '本地保存 {count} 个',
  saveCurrentDesign: '保存当前设计',
  savedDesignMeta: '{width}x{height} · {beads} 颗珠子',
  duplicateDesign: '复制 {title}',
  deleteDesign: '删除 {title}',
  noSavedDesigns: '没有已保存设计',
  ready: '就绪',
  localDraftPending: '本地草稿待保存',
  unableReadLocalDesigns: '无法读取本地设计',
  recoveredLocalDraft: '已恢复本地草稿',
  draftStorageUnavailable: '草稿存储不可用',
  savedLocallyAt: '已本地保存 {time}',
  localAutosaveFailed: '本地自动保存失败',
  unsupportedFileType: '不支持的文件类型',
  fileTooLarge: '文件超过 8 MB 原型限制',
  imageLoaded: '图片已载入',
  imageDecodeFailed: '无法解码图片',
  chooseImageFirst: '请先选择图片',
  uploadedPattern: '上传图案',
  patternGenerated: '图案已生成',
  generationFailed: '生成失败',
  templateLoaded: '模板已载入',
  recommendationApplied: '建议已应用',
  colorReplaced: '颜色已替换',
  designSaved: '设计已保存',
  designLoaded: '设计已载入',
  designDuplicated: '设计已复制',
  designDeleted: '设计已删除',
  blankGridCreated: '空白网格已创建',
  copyTitle: '{title} 副本',
  untitledBeadPattern: '未命名拼豆图案',
  fillRegion: '填充区域',
  eraseCells: '擦除格子',
  paintCells: '绘制格子',
  pdfMeta: '{width} x {height} 拼豆 · 共 {total} 颗珠子',
  pdfBeadCounts: '珠子统计',
  pdfFooter: '预览和统计由 Bead Studio AI 在本地生成。',
  csvColorId: '颜色 ID',
  csvColorName: '颜色名称',
  csvBrand: '品牌',
  csvHex: 'Hex',
  csvSymbol: '符号',
  csvQuantity: '数量',
  aiSubjectUpload: '上传的手工图片',
  aiSubjectTemplate: '选中的模板',
  aiReasonLarge: '源图细节较多，使用更大的网格能保留更清晰的边缘。',
  aiReasonBalanced: '这个尺寸能在细节和适合新手的珠子数量之间取得平衡。',
  aiColorReasonMore: '更多颜色可以在手动清理前减少浑浊区域。',
  aiColorReasonCurrent: '当前颜色上限适合生成干净的手工图案。',
  aiFeatureSilhouette: '主体轮廓',
  aiFeatureEdges: '高对比边缘',
  aiFeatureDetails: '较大的面部或标志细节',
  aiStrategyCrop: '围绕主体紧密裁剪',
  aiStrategyMerge: '合并细小噪点区域',
  aiStrategyPalette: '确保最终颜色适合拼豆色板',
  aiWarningLarge: '细节丰富的照片在快速模式生成后可能需要手动清理。'
};

const messages: Record<Locale, Record<MessageKey, string>> = {
  en,
  'zh-CN': zh
};

export const localeOptions: Array<{ value: Locale; label: string }> = [
  { value: 'en', label: 'English' },
  { value: 'zh-CN', label: '简体中文' }
];

export function detectBrowserLocale(languages?: readonly string[]): Locale {
  const requestedLanguages =
    languages ??
    (typeof navigator === 'undefined'
      ? []
      : navigator.languages.length
        ? navigator.languages
        : [navigator.language]);

  return requestedLanguages.some((language) => {
    const normalized = language.toLowerCase();
    return normalized === 'zh' || normalized.startsWith('zh-');
  })
    ? 'zh-CN'
    : 'en';
}

export function t(locale: Locale, key: MessageKey, values: Record<string, MessageValue> = {}) {
  return messages[locale][key].replace(/\{(\w+)\}/g, (_, name: string) =>
    values[name] === undefined ? '' : String(values[name])
  );
}

export function setDocumentLocale(locale: Locale) {
  if (typeof document === 'undefined') return;
  document.documentElement.lang = locale;
  document.title = t(locale, 'documentTitle');
  document
    .querySelector('meta[name="description"]')
    ?.setAttribute('content', t(locale, 'documentDescription'));
}

export function formatTime(locale: Locale, date: Date) {
  return date.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
}

const modeLabels: Record<Locale, Record<PatternMode, string>> = {
  en: {
    quick: 'Quick',
    ai_assist: 'AI Assist',
    ai_stylize: 'AI Stylize'
  },
  'zh-CN': {
    quick: '快速',
    ai_assist: 'AI 辅助',
    ai_stylize: 'AI 风格化'
  }
};

const backgroundLabels: Record<Locale, Record<BackgroundMode, string>> = {
  en: {
    transparent: 'Transparent',
    keep: 'Keep',
    solid: 'Solid white',
    simplify: 'Simplify'
  },
  'zh-CN': {
    transparent: '透明',
    keep: '保留',
    solid: '纯白',
    simplify: '简化'
  }
};

const toolLabels: Record<Locale, Record<EditorTool, string>> = {
  en: {
    pencil: 'Pencil',
    eraser: 'Eraser',
    fill: 'Fill',
    eyedropper: 'Eyedropper'
  },
  'zh-CN': {
    pencil: '画笔',
    eraser: '橡皮',
    fill: '填充',
    eyedropper: '取色器'
  }
};

const difficultyLabels: Record<Locale, Record<Difficulty, string>> = {
  en: {
    easy: 'easy',
    medium: 'medium',
    hard: 'hard'
  },
  'zh-CN': {
    easy: '简单',
    medium: '中等',
    hard: '困难'
  }
};

type TemplateCategory = TemplatePattern['category'] | 'all';

const categoryLabels: Record<Locale, Record<TemplateCategory, string>> = {
  en: {
    all: 'All',
    animals: 'Animals',
    icons: 'Icons',
    characters: 'Characters',
    flowers: 'Flowers',
    keychains: 'Keychains',
    coasters: 'Coasters'
  },
  'zh-CN': {
    all: '全部',
    animals: '动物',
    icons: '图标',
    characters: '角色',
    flowers: '花朵',
    keychains: '钥匙扣',
    coasters: '杯垫'
  }
};

const templateTitlesZh: Record<string, string> = {
  cat_orange: '橘猫',
  strawberry: '草莓',
  tiny_whale: '小鲸鱼',
  green_dino: '绿色小恐龙',
  daisy: '雏菊',
  gold_star: '金色星星',
  coral_heart: '珊瑚爱心',
  rainbow_arc: '彩虹拱',
  penguin: '企鹅',
  robot_pal: '机器人伙伴',
  mushroom: '蘑菇',
  butterfly: '蝴蝶',
  panda_face: '熊猫脸',
  ghost: '小幽灵',
  cupcake: '纸杯蛋糕',
  gold_key: '金钥匙',
  rocket: '火箭',
  pizza: '披萨片',
  turtle: '海龟',
  smiley: '笑脸'
};

const colorNamesZh: Record<string, string> = {
  transparent: '空',
  white: '白色',
  cream: '奶油色',
  black: '黑色',
  charcoal: '炭灰色',
  red: '红色',
  coral: '珊瑚色',
  orange: '橙色',
  yellow: '黄色',
  gold: '金色',
  green: '绿色',
  dark_green: '深绿色',
  mint: '薄荷绿',
  teal: '蓝绿色',
  blue: '蓝色',
  sky: '天蓝色',
  navy: '海军蓝',
  purple: '紫色',
  lavender: '薰衣草紫',
  pink: '粉色',
  magenta: '洋红色',
  brown: '棕色',
  tan: '棕褐色',
  skin: '肤色',
  gray: '灰色',
  light_gray: '浅灰色'
};

export function patternModeLabel(locale: Locale, mode: PatternMode) {
  return modeLabels[locale][mode];
}

export function backgroundModeLabel(locale: Locale, mode: BackgroundMode) {
  return backgroundLabels[locale][mode];
}

export function editorToolLabel(locale: Locale, tool: EditorTool) {
  return toolLabels[locale][tool];
}

export function difficultyLabel(locale: Locale, difficulty: Difficulty) {
  return difficultyLabels[locale][difficulty];
}

export function templateCategoryLabel(locale: Locale, category: TemplateCategory) {
  return categoryLabels[locale][category];
}

export function templateTitle(locale: Locale, template: TemplatePattern) {
  return locale === 'zh-CN' ? templateTitlesZh[template.id] ?? template.title : template.title;
}

export function colorName(locale: Locale, color: BeadColor) {
  return color.localizedName?.[locale] ?? (locale === 'zh-CN' ? colorNamesZh[color.id] ?? color.name : color.name);
}

export function csvHeaders(locale: Locale) {
  return [
    t(locale, 'csvColorId'),
    t(locale, 'csvColorName'),
    t(locale, 'csvBrand'),
    t(locale, 'csvHex'),
    t(locale, 'csvSymbol'),
    t(locale, 'csvQuantity')
  ];
}
